/**
 * Timing-step models:
 * - window mode (Phase A in-flow): seasonal planting window + frost
 * - plantDay mode (Phase C later): near-term best seed-down day
 */

import { parseFrostMonthDay } from './plantingGroundOverrideUtils';

const PAPER = '#f0eada';
const RUST = '#b4501c';
const INK = '#221e18';

const MONTH_SHORT = Object.freeze([
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
]);

export const PLANTING_SCHEDULES = Object.freeze({
  fall: {
    month: 'SEPTEMBER 2026',
    blanks: 2,
    days: 30,
    winStart: 15,
    best: 24,
    rainDay: 25,
    windowLabel: 'SEP 15 – OCT 20',
    bestDay: 'Thursday, September 24',
    bestShort: 'SEP 24',
    bestDow: 'THU 24',
    bestSoil: 70,
    rainAmount: 'six tenths of an inch',
    rainLabel: 'Sep 25',
    peak: 'DEC 3',
    dates: [
      'SEP 24 · DAY 0',
      'OCT 2 · DAY 8',
      'OCT 19 · DAY 25',
      'NOV 8 · DAY 45',
      'DEC 3 · DAY 70',
    ],
    forecast: [
      ['SUN 20', 74, '0%', '84 / 61', 0],
      ['MON 21', 75, '10%', '86 / 63', 8],
      ['TUE 22', 74, '20%', '82 / 64', 20],
      ['WED 23', 72, '40%', '79 / 62', 40],
      ['THU 24', 70, 'DRY', '77 / 58', 5],
      ['FRI 25', 68, '0.6 IN', '74 / 55', 90],
      ['SAT 26', 67, '30%', '76 / 54', 30],
      ['SUN 27', 68, '10%', '78 / 56', 10],
      ['MON 28', 69, '0%', '80 / 58', 0],
      ['TUE 29', 70, '10%', '81 / 60', 10],
    ],
  },
  spring: {
    month: 'APRIL 2027',
    blanks: 4,
    days: 30,
    winStart: 1,
    best: 2,
    rainDay: 3,
    windowLabel: 'MAR 25 – MAY 15',
    bestDay: 'Friday, April 2',
    bestShort: 'APR 2',
    bestDow: 'FRI 02',
    bestSoil: 64,
    rainAmount: 'half an inch',
    rainLabel: 'Apr 3',
    peak: 'JUN 11',
    dates: [
      'APR 2 · DAY 0',
      'APR 9 · DAY 7',
      'APR 27 · DAY 25',
      'MAY 17 · DAY 45',
      'JUN 11 · DAY 70',
    ],
    forecast: [
      ['WED 31', 58, '0%', '72 / 48', 0],
      ['THU 01', 60, '10%', '74 / 49', 10],
      ['FRI 02', 62, 'DRY', '75 / 50', 5],
      ['SAT 03', 63, '0.5 IN', '73 / 52', 85],
      ['SUN 04', 63, '30%', '70 / 49', 30],
      ['MON 05', 64, '10%', '74 / 51', 10],
      ['TUE 06', 65, '0%', '78 / 54', 0],
      ['WED 07', 65, '20%', '79 / 56', 20],
      ['THU 08', 66, '40%', '76 / 55', 40],
      ['FRI 09', 66, '10%', '80 / 57', 10],
    ],
  },
});

export function resolveScheduleKey(answers = {}) {
  return answers?.season === 'spring' ? 'spring' : 'fall';
}

export function getPlantingSchedule(answers = {}) {
  return PLANTING_SCHEDULES[resolveScheduleKey(answers)];
}

function parseIsoParts(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!y || !m || !d) return null;
  return { y, m, d, iso: `${match[1]}-${match[2]}-${match[3]}` };
}

function formatShortMd(iso) {
  const parts = parseIsoParts(iso);
  if (!parts) return '';
  return `${MONTH_SHORT[parts.m - 1]} ${parts.d}`;
}

function formatLongDate(iso) {
  const parts = parseIsoParts(iso);
  if (!parts) return '';
  const dt = new Date(parts.y, parts.m - 1, parts.d);
  return dt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatMonthTitle(y, m) {
  const dt = new Date(y, m - 1, 1);
  const name = dt.toLocaleDateString('en-US', { month: 'long' }).toUpperCase();
  return `${name} ${y}`;
}

function formatWindowLabel(startIso, endIso) {
  const start = formatShortMd(startIso);
  const end = formatShortMd(endIso);
  if (start && end) return `${start} – ${end}`;
  return start || end || '';
}

/** Bar width as day-of-year so last spring frost sits left of first fall frost. */
export function frostCalendarPct(label) {
  const { month, day } = parseFrostMonthDay(label);
  if (!month || !day) return 0;
  const doy = Math.floor((Date.UTC(2026, month - 1, day) - Date.UTC(2026, 0, 1)) / 86400000) + 1;
  return Math.max(4, Math.min(100, Math.round((doy / 365) * 100)));
}

function frostDisplayValue(label) {
  return String(label || '').trim().toUpperCase() || '—';
}

const FROST_BOUNDS_NOTE =
  'Frost dates are USDA hardiness zone averages for this location — not site microclimate.';

function buildFrostBoundBars(frost = null) {
  const lastFrost = frost?.last_frost_label || '';
  const firstFrost = frost?.first_frost_label || '';
  return [
    {
      label: 'LAST SPRING FROST',
      value: frostDisplayValue(lastFrost),
      pct: lastFrost ? `${frostCalendarPct(lastFrost)}%` : '0%',
      fill: '#4e7ea8',
    },
    {
      label: 'FIRST FALL FROST',
      value: frostDisplayValue(firstFrost),
      pct: firstFrost ? `${frostCalendarPct(firstFrost)}%` : '0%',
      fill: '#b4501c',
    },
  ];
}

function frostBoundsNote(frost = null) {
  const lastFrost = frost?.last_frost_label || '';
  const firstFrost = frost?.first_frost_label || '';
  if (lastFrost && firstFrost) {
    return `Frost-free stretch runs from ${lastFrost} through ${firstFrost}. These are USDA hardiness zone averages for this location — not site microclimate.`;
  }
  return FROST_BOUNDS_NOTE;
}

function buildStubTimingModel({ answers = {}, cropName = 'oats' } = {}) {
  const sched = getPlantingSchedule(answers);
  const crop = String(cropName || 'oats').replace(/\s*\(nurse\)\s*/i, '').toLowerCase();

  const dowHeads = ['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label) => ({ label }));
  const calCells = [];
  for (let i = 0; i < sched.blanks; i += 1) {
    calCells.push({
      day: '',
      mark: '',
      bg: 'transparent',
      border: 'none',
      fg: INK,
      size: '15px',
      markFg: INK,
      empty: true,
    });
  }
  for (let d = 1; d <= sched.days; d += 1) {
    const inWin = d >= sched.winStart;
    const best = d === sched.best;
    const rain = d === sched.rainDay;
    calCells.push({
      day: String(d),
      mark: best ? 'PLANT' : rain ? 'RAIN' : '',
      bg: best ? RUST : inWin ? '#d8cfae' : PAPER,
      border: best ? '2.5px solid #221e18' : '2px solid #221e18',
      fg: best ? '#f5efdf' : INK,
      markFg: best ? '#f5efdf' : '#4e7ea8',
      size: best ? '18px' : '15px',
      empty: false,
      isBest: best,
      isRain: rain,
    });
  }

  const forecast = sched.forecast.map(([day, soil, rain, air, pct]) => {
    const best = day === sched.bestDow;
    return {
      day,
      soil: `${soil}°`,
      rain: rain === 'DRY' ? 'DRY' : rain,
      air,
      rainPct: `${pct}%`,
      bg: best ? '#2f3f2e' : PAPER,
      fg: best ? '#d9773c' : INK,
      lab: best ? '#9bae93' : '#6b6350',
      isBest: best,
    };
  });

  return {
    seasonKey: resolveScheduleKey(answers),
    bestDay: sched.bestDay,
    bestDayShort: sched.bestShort,
    bestWhy: `Soil sits at ${sched.bestSoil}°F — right in the germination band for ${crop} — and ${sched.rainAmount} of rain arrives the following afternoon. Seed goes down dry and gets watered in.`,
    yearChip: `${String(resolveScheduleKey(answers)).toUpperCase()} WINDOW`,
    windowLabel: sched.windowLabel,
    calMonth: sched.month,
    dowHeads,
    calCells,
    calLegend: [
      { label: 'WINDOW', color: '#d8cfae' },
      { label: 'BEST DAY', color: RUST },
      { label: 'RAIN', color: PAPER },
    ],
    forecast,
    showForecastStrip: true,
    forecastAccent: 'SOIL TEMP · RAIN',
    rainDelta: 'ZONE AVERAGE',
    rainBars: buildFrostBoundBars(),
    rainNote: FROST_BOUNDS_NOTE,
    dates: sched.dates,
    peak: sched.peak,
    live: false,
    forecastStatus: 'stub',
    mode: 'plantDay',
  };
}

function buildLiveCalendar({
  windowStart,
  windowEnd,
  bestIso = null,
  rainIsos = [],
  focusIso,
  markBestAndRain = true,
}) {
  const focus = parseIsoParts(focusIso)
    || parseIsoParts(bestIso)
    || parseIsoParts(windowStart)
    || parseIsoParts(windowEnd);
  if (!focus) return { calMonth: '', calCells: [] };

  const { y, m } = focus;
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDow = new Date(y, m - 1, 1).getDay();
  const winStart = parseIsoParts(windowStart);
  const winEnd = parseIsoParts(windowEnd);
  const bestParts = markBestAndRain ? parseIsoParts(bestIso) : null;
  const rainSet = markBestAndRain
    ? new Set((rainIsos || []).map((iso) => parseIsoParts(iso)?.iso).filter(Boolean))
    : new Set();

  const inWindow = (dayNum) => {
    if (!winStart || !winEnd) return false;
    const t = Date.UTC(y, m - 1, dayNum);
    const a = Date.UTC(winStart.y, winStart.m - 1, winStart.d);
    const b = Date.UTC(winEnd.y, winEnd.m - 1, winEnd.d);
    return t >= a && t <= b;
  };

  const calCells = [];
  for (let i = 0; i < firstDow; i += 1) {
    calCells.push({
      day: '',
      mark: '',
      bg: 'transparent',
      border: 'none',
      fg: INK,
      size: '15px',
      markFg: INK,
      empty: true,
    });
  }

  for (let d = 1; d <= daysInMonth; d += 1) {
    const best = Boolean(
      bestParts && bestParts.y === y && bestParts.m === m && bestParts.d === d,
    );
    const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const rain = !best && rainSet.has(iso);
    const win = inWindow(d);
    calCells.push({
      day: String(d),
      mark: best ? 'PLANT' : rain ? 'RAIN' : '',
      bg: best ? RUST : win ? '#d8cfae' : PAPER,
      border: best ? '2.5px solid #221e18' : '2px solid #221e18',
      fg: best ? '#f5efdf' : INK,
      markFg: best ? '#f5efdf' : '#4e7ea8',
      size: best ? '18px' : '15px',
      empty: false,
      isBest: best,
      isRain: rain,
    });
  }

  return {
    calMonth: formatMonthTitle(y, m),
    calCells,
  };
}

const PLANT_WEEK_NOTE =
  'When you are inside (or near) this window, My Plans will show good work days from the near-term forecast — separate from planning the season.';

function buildWindowTimingModel({
  answers = {},
  cropName = 'oats',
  forecast = null,
  forecastStatus = null,
} = {}) {
  const stub = buildStubTimingModel({ answers, cropName });
  const seasonKey = resolveScheduleKey(answers);
  const seasonLabel = seasonKey === 'spring' ? 'SPRING' : 'FALL';
  const zone = (forecast?.frost?.zone_code || '').toString().toUpperCase();
  const window = forecast?.window || null;
  const frost = forecast?.frost || null;

  const hasWindow = Boolean(window?.start && window?.end);
  const windowLabel = hasWindow
    ? (formatWindowLabel(window.start, window.end) || stub.windowLabel)
    : stub.windowLabel;

  const focusIso = window?.start || frost?.first_frost_date || frost?.last_frost_date;
  let calMonth;
  let calCells;
  if (hasWindow) {
    ({ calMonth, calCells } = buildLiveCalendar({
      windowStart: window.start,
      windowEnd: window.end,
      focusIso,
      markBestAndRain: false,
    }));
  } else {
    calMonth = stub.calMonth;
    // Keep window shading from stub; strip PLANT/RAIN marks.
    calCells = stub.calCells.map((cell) => {
      if (cell.empty) return cell;
      const inWin = cell.bg === '#d8cfae' || cell.isBest || cell.bg === RUST;
      return {
        ...cell,
        mark: '',
        isBest: false,
        isRain: false,
        bg: inWin ? '#d8cfae' : PAPER,
        fg: INK,
        size: '15px',
        border: '2px solid #221e18',
        markFg: INK,
      };
    });
  }

  const firstFrost = frost?.first_frost_label || '';
  const lastFrost = frost?.last_frost_label || '';
  const crop = String(cropName || 'oats').replace(/\s*\(nurse\)\s*/i, '').toLowerCase();

  let why;
  if (forecastStatus === 'loading') {
    why = 'Resolving the planting window for this zone…';
  } else if (forecastStatus === 'missing') {
    why = 'Add a town or map plot on Ground so we can set the zone planting window.';
  } else     if (hasWindow && (firstFrost || lastFrost)) {
    const frostKind = frost?.source === 'manual' ? 'Your' : 'Zone-average';
    if (seasonKey === 'spring') {
      why = `${frostKind} last frost ${lastFrost || '—'}. Plant ${crop} after that date through ${formatShortMd(window.end)} so the stand has warm ground and runway.`;
    } else {
      why = `${frostKind} first frost ${firstFrost || '—'}. Put ${crop} down in this band so cool-season seed establishes before frost.`;
    }
    why = `${why} ${PLANT_WEEK_NOTE}`;
  } else {
    why = `Seasonal ${seasonLabel.toLowerCase()} window for planning. ${PLANT_WEEK_NOTE}`;
  }

  let yearChip = `${seasonLabel} WINDOW`;
  if (zone) yearChip = `ZONE ${zone} · ${seasonLabel}`;
  if (forecastStatus === 'loading') yearChip = 'READING WINDOW';
  if (forecastStatus === 'missing') yearChip = 'NO LOCATION';

  const frostBars = buildFrostBoundBars(frost);

  return {
    ...stub,
    mode: 'window',
    bestDay: windowLabel,
    bestDayShort: windowLabel,
    bestWhy: why,
    yearChip,
    windowLabel,
    calMonth: calMonth || stub.calMonth,
    calCells: calCells.length ? calCells : stub.calCells,
    calLegend: [{ label: 'WINDOW', color: '#d8cfae' }],
    forecast: [],
    showForecastStrip: false,
    forecastAccent: '',
    rainDelta: frost?.source === 'manual' ? 'YOUR OVERRIDE' : 'ZONE AVERAGE',
    rainBars: frostBars,
    rainNote: frostBoundsNote(frost),
    plantWeekNote: PLANT_WEEK_NOTE,
    live: hasWindow,
    forecastStatus: hasWindow ? 'window' : (forecastStatus || 'stub'),
    frost,
    window: window || null,
  };
}

/** Near-term plant-day model — reserved for Phase C library plant-week. */
export function buildPlantDayTimingModel({ answers = {}, cropName = 'oats', forecast = {} } = {}) {
  const stub = buildStubTimingModel({ answers, cropName });
  const days = Array.isArray(forecast.days) ? forecast.days : [];
  const best = forecast.best_day || null;
  const window = forecast.window || {};
  const bestIso = best?.date || null;
  const rainIsos = days.filter((d) => d.is_rain_mark).map((d) => d.date);
  const focusIso = bestIso || forecast.as_of || window.start;
  const { calMonth, calCells } = buildLiveCalendar({
    windowStart: window.start,
    windowEnd: window.end,
    bestIso,
    rainIsos,
    focusIso,
    markBestAndRain: true,
  });

  const anySoilProxy = days.some((d) => d.soil_is_proxy);
  const forecastRows = days.map((d) => {
    const isBest = Boolean(d.is_best);
    const soilNum = d.soil_display != null ? d.soil_display : null;
    return {
      day: d.label || formatShortMd(d.date),
      soil: soilNum != null ? `${soilNum}°` : '—',
      rain: d.rain_label || '—',
      air: d.air_label || '—',
      rainPct: `${Number(d.rain_pct) || 0}%`,
      bg: isBest ? '#2f3f2e' : PAPER,
      fg: isBest ? '#d9773c' : INK,
      lab: isBest ? '#9bae93' : '#6b6350',
      isBest,
    };
  });

  const windowLabel = formatWindowLabel(window.start, window.end) || stub.windowLabel;
  const bestDayLong = bestIso ? formatLongDate(bestIso) : stub.bestDay;
  const bestShort = bestIso ? formatShortMd(bestIso) : stub.bestDayShort;
  let bestWhy = best?.why || stub.bestWhy;
  if (best?.outside_window) {
    bestWhy = `${bestWhy} This pick sits outside the ideal zone window — plant sooner if you can, or wait for the window.`;
  }

  let yearChip = 'LIVE FORECAST';
  if (best?.outside_window) yearChip = 'OUTSIDE WINDOW';

  return {
    ...stub,
    mode: 'plantDay',
    bestDay: bestDayLong,
    bestDayShort: bestShort,
    bestWhy,
    yearChip,
    windowLabel,
    calMonth: calMonth || stub.calMonth,
    calCells: calCells.length ? calCells : stub.calCells,
    forecast: forecastRows.length ? forecastRows : stub.forecast,
    showForecastStrip: true,
    forecastAccent: anySoilProxy ? 'AIR TEMP · RAIN' : 'SOIL TEMP · RAIN',
    rainDelta: 'NEAR-TERM',
    rainBars: buildFrostBoundBars(forecast.frost),
    rainNote:
      'Best plant day uses the live forecast, zone window, and drainage. Frost bounds below are zone averages.',
    live: true,
    forecastStatus: 'live',
    frost: forecast.frost || null,
    window,
  };
}

/**
 * @param {'window'|'plantDay'} [opts.mode] default window (Phase A Timing)
 */
export function buildTimingModel({
  answers = {},
  cropName = 'oats',
  forecastPayload = null,
  forecastStatus = null,
  mode = 'window',
} = {}) {
  if (mode === 'plantDay') {
    if (
      forecastPayload
      && forecastPayload.weather_ok
      && Array.isArray(forecastPayload.days)
      && forecastPayload.days.length > 0
    ) {
      return buildPlantDayTimingModel({ answers, cropName, forecast: forecastPayload });
    }
    return buildStubTimingModel({ answers, cropName });
  }

  return buildWindowTimingModel({
    answers,
    cropName,
    forecast: forecastPayload,
    forecastStatus,
  });
}
